import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { IconDroplet, IconArrowRight, IconAlertTriangle } from '@tabler/icons-react';

export default function DervOverview() {
  const navigate = useNavigate();
  const { org, orgId, userProfile } = useAuth();
  const [branchTanks, setBranchTanks] = useState([]);
  const [loading, setLoading] = useState(true);

  const resolvedOrgId = orgId || userProfile?.orgId;
  const brandColour = org?.brandColour || 'var(--color-brand)';

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) {
        setLoading(false);
        return;
      }
      try {
        const branchesSnap = await getDocs(
          collection(db, 'organisations', resolvedOrgId, 'branches')
        );
        const branches = branchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const enriched = await Promise.all(branches.map(async (branch) => {
          const tanksSnap = await getDocs(
            collection(db, 'organisations', resolvedOrgId, 'branches', branch.id, 'dervTanks')
          );
          const tanks = tanksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          return { ...branch, tanks };
        }));

        setBranchTanks(enriched.filter(b => b.tanks.length > 0));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [resolvedOrgId]);

  const totalTanks = branchTanks.reduce((sum, b) => sum + b.tanks.length, 0);

  const statusInfo = (pct, threshold) => {
    if (pct <= 10) return { label: 'Critical', colour: 'var(--color-danger)', bg: 'var(--color-danger-light)', border: 'var(--color-danger)' };
    if (pct <= (threshold || 15)) return { label: 'Order required', colour: '#E8610A', bg: '#FDF0E8', border: '#E8610A' };
    return { label: 'Good', colour: 'var(--color-success)', bg: 'var(--color-success-light)', border: 'var(--color-border-light)' };
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Derv tanks</h1>
        <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
          All branches · {loading ? '...' : `${totalTanks} tank${totalTanks !== 1 ? 's' : ''}`}
        </p>

        {loading ? (
          <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', padding: '32px 0', textAlign: 'center' }}>Loading tanks...</div>
        ) : branchTanks.length === 0 ? (
          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '48px 32px', textAlign: 'center' }}>
            <IconDroplet size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>No tanks yet</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Tanks are added by branch managers during onboarding</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
            {branchTanks.map(branch =>
              branch.tanks.map(tank => {
                const pct = Math.round((tank.currentLitres / tank.capacityLitres) * 100);
                const status = statusInfo(pct, tank.orderThresholdPct);
                const daysRemaining = Math.round(tank.currentLitres / 120);
                return (
                  <div
                    key={tank.id}
                    onClick={() => navigate(`/fuel/${tank.id}`)}
                    style={{
                      background: 'var(--color-bg-primary)',
                      border: `0.5px solid ${status.border}`,
                      borderRadius: 'var(--radius-lg)',
                      padding: '20px 22px',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => pct > 10 && (e.currentTarget.style.borderColor = 'var(--color-border-medium)')}
                    onMouseLeave={e => pct > 10 && (e.currentTarget.style.borderColor = status.border)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 2 }}>{branch.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{tank.name}</div>
                      </div>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: status.bg, color: status.colour, display: 'flex', alignItems: 'center', gap: 4 }}>
                        {pct <= 30 && <IconAlertTriangle size={10} stroke={1.5} />}
                        {status.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: 40, fontWeight: 400, color: status.colour, lineHeight: 1 }}>{pct}%</div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textAlign: 'right' }}>
                        <div>{tank.currentLitres?.toLocaleString()} L</div>
                        <div>of {tank.capacityLitres?.toLocaleString()} L</div>
                      </div>
                    </div>
                    <div style={{ height: 6, background: 'var(--color-bg-secondary)', borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: status.colour, borderRadius: 3, transition: 'width 0.5s ease' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: 'var(--color-text-tertiary)' }}>
                        {daysRemaining > 0 ? `~${daysRemaining} days remaining` : 'Less than 1 day remaining'}
                      </span>
                      <span style={{ color: brandColour, display: 'flex', alignItems: 'center', gap: 3 }}>
                        View <IconArrowRight size={13} stroke={1.5} />
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}